import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ReplaySubject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { CurrentView } from 'app/shared/modals/add-livestream-modal/add-livestream-modal.types';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import User from 'entity/User';
import { Presenter, PresenterType, Livestreams } from 'messages/websocket.messages';

import { PresentationSettings } from '../../../../../messages/websocket.messages';
import { ModalDialogComponent, ModalDialogData, ModalDialogResult } from '../../modal-dialog/modal-dialog.component';
import { ActionMenuService } from '../../services/action-menu.service';
import { LivestreamService } from '../../services/livestream/livestream.service';
import { PresentationService } from '../../services/presentation/presentation.service';
import { SceneService } from '../../services/scene.service';
import { SideSectionService } from '../../services/side-section.service';
import { selectors, socialAppSelectors } from '../../state/app';
import { SideContainerType } from '../enums/SideContainerType';

@Component({
  selector: 'app-manage-presenters',
  templateUrl: './manage-presenters.component.html',
  styleUrls: ['./manage-presenters.component.scss'],
  standalone: false,
})
export class ManagePresentersComponent extends Loggable implements OnInit, OnDestroy {
  public isPresentationFull = false;
  public isLivestreamActive = false;
  public isConnectedRoom = false;
  public isCrossRoom = false;
  public isBroadcasting = false;
  public presenters: Record<string, Presenter> = {};
  public invitedPresenters: Record<string, User> = {};
  public queuedLivestreams: Livestreams = {};
  public isLiveStreamEnabled = false;
  private presentationSettings?: PresentationSettings;
  protected isSomeoneElsePresenting = false;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private user?: User;

  public constructor(
    private actionMenuService: ActionMenuService,
    private sideSectionService: SideSectionService,
    private store: Store,
    private dialog: MatDialog,
    private presentationService: PresentationService,
    private sceneService: SceneService,
    private livestreamService: LivestreamService,
  ) {
    super();
    this.setDisplayName('ManagePresentersComponent');
  }

  public ngOnInit(): void {
    this.store
      .select(selectors.isPresentationFull)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationFull) => (this.isPresentationFull = isPresentationFull));

    this.store
      .select(socialAppSelectors.isPresentationActiveInAnotherRoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActiveInAnotherRoom) => {
        this.isSomeoneElsePresenting = isPresentationActiveInAnotherRoom;
      });

    this.store
      .select(selectors.isLivestreamActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isLivestreamActive) => (this.isLivestreamActive = isLivestreamActive));

    this.store
      .select(selectors.isConnectedRoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isConnectedRoom) => (this.isConnectedRoom = isConnectedRoom));

    this.store
      .select(selectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => (this.isCrossRoom = isCrossRoom));

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((userEntity) => (this.user = userEntity.user));

    this.store
      .select(selectors.getPresentation)
      .pipe(
        filter((presentation) => notNil(presentation)),
        takeUntil(this.destroyed$),
      )
      .subscribe((presentation) => {
        this.presenters = presentation?.presenters ?? {};
        this.parseInvitedPresenters(presentation?.requestedPresenterIds ?? []);
      });

    this.store
      .select(selectors.getPresentationSettings)
      .pipe(
        filter((settings) => !!settings),
        takeUntil(this.destroyed$),
      )
      .subscribe((presentationSettings) => (this.presentationSettings = presentationSettings));

    this.store
      .select(selectors.getQueuedLivestreams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((livestreams) => {
        this.queuedLivestreams = livestreams.queuedLivestreams ?? {};
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_live_stream_management)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((liveStreamEnabled) => (this.isLiveStreamEnabled = liveStreamEnabled));
  }

  public onCrossRoomToggle(): void {
    const title = `Are you sure you want to present to ${
      this.isCrossRoom ? 'all connected rooms' : 'only the current room?'
    }`;
    const text = this.isCrossRoom
      ? 'Switching this setting will add this presentation to other connected rooms and will cancel all other presentations that may be in progress.'
      : 'Switching this setting will remove this presentation from other connected rooms.';
    const data: ModalDialogData = {
      title,
      text,
      primaryButtonText: 'Continue',
      secondaryButtonText: 'Cancel',
    };

    this.logUserAction(this.displayName, 'onCrossRoomToggle');

    this.dialog
      .open<ModalDialogComponent, ModalDialogData, ModalDialogResult>(ModalDialogComponent, {
        data,
        width: '540px',
        panelClass: 'mat-dialog-custom-container',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: async (result) => {
          // if the user cancels the dialog, revert the toggle
          if (result !== ModalDialogResult.PRIMARY) {
            this.isCrossRoom = !this.isCrossRoom;
            return;
          }
          this.logUserAction(this.displayName, 'onCrossRoomToggle accepted', this.isCrossRoom);
          await this.changePresentationType(this.isCrossRoom);
        },
      });
  }

  public addPresenter(): void {
    this.responsiveMenuValidation();
    this.actionMenuService.handlePresenter();
  }

  public getPresenterName(presenter: Presenter | User): string {
    return formatUserName(presenter);
  }

  public async removePresenter(presenter: Presenter): Promise<void> {
    const { presenterType, userId: id } = presenter;
    const ids = Object.keys(this.presenters);
    if (ids.length === 1 && ids.includes(id)) {
      this.endPresentation(id, presenterType);
      return;
    }

    if (presenterType === PresenterType.LIVE_STREAM) {
      await this.livestreamService.removeActiveLivestream(presenter).catch((e) => this.error(e));
      return;
    }

    try {
      await this.presentationService.removeActivePresenter(id);
    } catch (e) {
      // FIXME: show the user an error
      this.error('Failed to remove active presenter', e);
    }
  }

  private endPresentation(id: string, presenterType?: PresenterType): void {
    if (presenterType === PresenterType.LIVE_STREAM) {
      this.removeLivestream(id);
    }
    this.clearQueue();
    this.presentationService.endPresentation().catch((e) => {
      this.error('Failed to end presentation', e);
    });
  }

  private clearQueue(): void {
    Object.keys(this.queuedLivestreams).forEach((id) => this.livestreamService.removeLivestreamFromQueue(id));
  }

  public removeInvitedPresenter(id: string): void {
    this.presentationService.removeInvitedPresenter(id).catch((e) => {
      this.error('Failed to remove invited presenter', e);
    });
  }

  public addLivestream(): void {
    const view = CurrentView.MANAGE_PRESENTERS;
    this.livestreamService.openAddLivestreamModal(view);
  }

  public removeLivestream(id: string): void {
    this.livestreamService.removeLivestreamSubscriber(id);
    this.livestreamService.removeLivestreamFromQueue(id);
  }

  public previewLivestream(livestream: Presenter): void {
    const view = CurrentView.MANAGE_PRESENTERS;
    this.livestreamService.openAddLivestreamModal(view, livestream);
  }

  public getLivestreamTitle(livestream: Presenter): string {
    const title = livestream?.firstName ?? 'Livestream';
    return title;
  }

  public isLivestreamBroadcasting(streamId: string): boolean {
    return !!this.presenters[streamId];
  }

  public startBroadcast(livestream: Presenter) {
    this.livestreamService.addActiveLivestream(livestream, this.isCrossRoom);
  }

  public async queueBroadcast(livestream: Presenter) {
    const ids = Object.keys(this.presenters);
    if (ids.length === 1 && ids.includes(livestream.userId)) {
      this.presentationService.endPresentation().catch((e) => {
        this.error('Failed to end presentation', e);
      });
    } else {
      await this.livestreamService
        .moveLivestreamBackToQueue(livestream)
        .catch((e) => this.error('Failed to move livestream back into queue', e));
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  /**
   * Takes an array of Ids and feed a key-value pair with their respective user information
   *
   * @param presenterIds Ids array of people invited to present
   */
  private parseInvitedPresenters(presenterIds: string[]): void {
    this.invitedPresenters = {};
    presenterIds.forEach(
      (id) => (this.invitedPresenters[id] = this.sceneService.sceneManager?.participants.get(id)?.user ?? {}),
    );
  }

  /**
   * Changes the type of the presentation, if a cross-room presentation is selected
   * it requests to start a presentation only for the other rooms, if switching back to
   * local presentation, it sends a request to end the presentation to the others.
   *
   * Also, to switch to a cross-room presentation, update the local presentation to
   * update local store with the cross-room property as true.
   * @returns
   */
  private async changePresentationType(crossRoom: boolean): Promise<void> {
    if (!this.user) {
      this.error('Attempting to switch presentation type without user id.');
      return;
    }

    // Moving from Single --> CrossRoom
    // Start New Presentation
    if (crossRoom) {
      this.info('Switching to cross room presentation. Setting state to NEVER');
      this.presentationService
        .changePresentationSingleToCrossRoom({
          selectedPresenters: [],
          activePresenters: this.presenters,
          settings: this.presentationSettings,
        })
        .catch((e) => {
          this.error('Failed to change presentation type: Single -> Cross Room', e);
        });

      // If livestream publish
      Object.values(this.presenters).forEach((presenter) => {
        if (presenter.presenterType === PresenterType.LIVE_STREAM) {
          this.livestreamService.addActiveLivestream(presenter, true);
        }
      });
    }

    // Moving from Cross Room --> Single
    // End Presentation for Cross rooms
    if (!crossRoom) {
      this.info('Switching to single room presentation.');
      this.presentationService.changePresentationCrossToSingleRoom().catch((e) => {
        this.error('Failed to change presentation type: Cross -> Single Room', e);
      });

      // Unpublish livestreams from from cross-room but don't unpublished
      Object.values(this.presenters)
        .filter((p) => p.presenterType === PresenterType.LIVE_STREAM)
        .forEach(async (ls) => {
          await this.livestreamService.removeActiveLivestream(ls, true).catch((e) => this.error(e));
        });
    }

    // Update the Mix, Screenshare, Presenters etc...
    await this.presentationService.updatePresentationType({ crossRoom });
  }

  private responsiveMenuValidation(): void {
    if (window.innerWidth < 700 || window.innerHeight < 650) {
      this.sideSectionService.setOpenState(SideContainerType.NONE);
    }
  }
}
